/**
 * BUSCA, ORDENAÇÃO, RECORTE e RESUMO das listas do FINANCEIRO — as abas
 * "A receber" e "A pagar" de `/financeiro`.
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters`, do
 * `serviceOrdersListFilters` e do `receiptsListFilters`: são regras que decidem
 * QUAIS lançamentos o prestador vê e em que ordem, e todas erram em silêncio —
 * uma lista recortada errado não dá erro, só esconde dinheiro.
 *
 * ─── O QUE É ESPECÍFICO DESTAS LISTAS ───────────────────────────────────────
 *
 * · **A ORDEM É A DATA, porque a lista é uma AGENDA.** As outras listas do app
 *   oferecem "maior valor"/"menor valor"; aqui a tela agrupa por dia de
 *   vencimento e carimba cada grupo com "Vencido"/"Hoje"/"Em 3 dias" — ordenar
 *   por valor desmontaria o agrupamento que é a própria estrutura da tela. As
 *   duas opções são as duas direções que a agenda admite, e a pergunta "quais
 *   são os maiores?" é respondida pela FAIXA DE VALOR do modal, que não
 *   conflita com nada. É o corolário do #198: não se transfere para uma tela
 *   uma feature que a estrutura dela não comporta.
 *
 * · **O status EFETIVO vem da view (`computed_status`), não da coluna.**
 *   `finance_entries_with_status` calcula o atraso no servidor. Recortar por
 *   `status` cru faria "Vencidos" devolver ZERO — a coluna guarda `pending` até
 *   alguém dar baixa. A leitura passa por `effectiveFinanceStatus`, o mesmo
 *   helper do agregado do mês (`financeOverview`).
 *
 * · **O rótulo do status DEPENDE DO TIPO.** `paid` é "Recebidos" numa entrada e
 *   "Pagos" numa saída: é o vocabulário que a tela inteira já usa. Um rótulo só
 *   para os dois obrigaria o usuário a traduzir mentalmente em metade das
 *   telas.
 *
 * · **O PERÍODO OLHA PARA A FRENTE.** Os presets de `reportPeriod` são de
 *   relatório e miram o passado ("Últimos 30 dias"); aqui a coluna é
 *   `due_date`, e a pergunta do prestador é "o que vence agora". Por isso os
 *   presets são próprios, com "Próximos 7/30 dias" — que é, aliás, o que o
 *   seletor antigo desta tela já oferecia e não podia ser perdido.
 *
 * · **As datas são DATE ('YYYY-MM-DD') e se comparam como STRING.** A fronteira
 *   com `Date` passa por `todayIsoDate` (calendário LOCAL): `new Date(due_date)`
 *   é meia-noite UTC e em UTC-3 cai no DIA ANTERIOR (#36/#114).
 */
import { addDays, endOfMonth, endOfYear, startOfMonth, startOfYear, subDays } from 'date-fns';
import { normalizeSearchText } from '@/lib/quoteSearch';
import { quoteTotalNumber } from '@/lib/quotesListSummary';
import { matchesListRange, type ListValueRange } from '@/lib/listFilters';
import { todayIsoDate } from '@/lib/financeStatus';
import { effectiveFinanceStatus } from '@/lib/financeOverview';
import type { FinanceKind, FinanceStatus } from '@/types/database';

/**
 * O que este módulo enxerga de um lançamento.
 *
 * `finance_entries` NÃO tem `sequence_id` — por isso não há busca por `#N` aqui,
 * ao contrário das listas de documento. Declarar o campo daria ao leitor a
 * impressão de que dá para consultá-lo (o mesmo cuidado do `status` em
 * `receiptsListFilters`, #57).
 */
export interface FinanceEntryRow {
  id?: string | null;
  description?: string | null;
  amount?: unknown;
  due_date?: string | null;
  status?: string | null;
  computed_status?: string | null;
  kind?: string | null;
}

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo (é o estado sem filtro).
 *
 * Só a DESCRIÇÃO é varrida — é o único texto que o card mostra. Buscar em campo
 * que a linha não exibe (as `notes`, por exemplo) devolveria resultados que o
 * usuário não consegue explicar.
 */
export function financeEntryMatchesSearch(entry: FinanceEntryRow, term: string): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;
  return normalizeSearchText(entry?.description).includes(needle);
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type FinanceSortOption = 'dueSoon' | 'dueLate';

export const FINANCE_SORT_LABELS: Record<FinanceSortOption, string> = {
  dueSoon: 'Vencimento mais próximo',
  dueLate: 'Vencimento mais distante',
};

export const FINANCE_SORT_ORDER: readonly FinanceSortOption[] = ['dueSoon', 'dueLate'];

/**
 * `dueSoon` é o PADRÃO porque é como a lista sempre se comportou (a agenda já
 * subia em ordem crescente de vencimento). Trocar o padrão junto com o
 * redesenho mudaria a tela de todo mundo sem ninguém ter pedido.
 */
export const DEFAULT_FINANCE_SORT: FinanceSortOption = 'dueSoon';

/** Não muta a entrada: a lista da tela é derivada da consulta. */
export function sortFinanceEntriesBy<T extends FinanceEntryRow>(
  entries: readonly T[],
  option: FinanceSortOption,
): T[] {
  const rows = [...entries];
  // Desempate ESTÁVEL por id (#93): vários lançamentos no MESMO dia é a regra,
  // não a exceção (é o que o agrupamento por data pressupõe) — sem ele a lista
  // "pisca" entre renders sem ninguém ter mexido em nada.
  const byId = (a: T, b: T) => String(a?.id).localeCompare(String(b?.id));
  const dateOf = (entry: T) => String(entry?.due_date ?? '');

  if (option === 'dueLate') {
    return rows.sort((a, b) => dateOf(b).localeCompare(dateOf(a)) || byId(a, b));
  }
  return rows.sort((a, b) => dateOf(a).localeCompare(dateOf(b)) || byId(a, b));
}

// ─── Recorte por STATUS (os botões da faixa) ────────────────────────────────

export type FinanceStatusFilter = 'all' | FinanceStatus;

/**
 * Os botões da faixa, na ordem do ciclo de vida do lançamento.
 *
 * O rótulo de `paid` depende do TIPO (ver o cabeçalho). Os cinco CABEM em uma
 * linha em 360px, e isso foi medido por réplica de DOM com a fonte real
 * (`text-[11px]` + `px-1.5`, gaps incluídos, contra os 328px úteis):
 *
 *   Todos · Pendentes · Recebidos · Vencidos · Cancelados → 319,3 ✓
 *   Todos · Pendentes · Pagos     · Vencidos · Cancelados → 298,2 ✓
 *
 * A contagem não entra no rótulo justamente por isso: ela é o que estourava a
 * faixa em #191, e vai para o `title`/`aria-label`, onde não custa pixel.
 */
export function financeStatusFilterOptions(
  kind: FinanceKind,
): readonly { value: FinanceStatusFilter; label: string }[] {
  return [
    { value: 'all', label: 'Todos' },
    { value: 'pending', label: 'Pendentes' },
    { value: 'paid', label: kind === 'income' ? 'Recebidos' : 'Pagos' },
    { value: 'overdue', label: 'Vencidos' },
    { value: 'canceled', label: 'Cancelados' },
  ];
}

export function matchesFinanceStatus(
  entry: FinanceEntryRow,
  filter: FinanceStatusFilter,
  today: string = todayIsoDate(),
): boolean {
  if (filter === 'all') return true;
  return effectiveFinanceStatus(entry, today) === filter;
}

/**
 * Quantos lançamentos cada botão traria — contados sobre o recorte JÁ filtrado
 * por busca, período e valor, mas ANTES do status.
 *
 * É a única definição que não mente: contar sobre a lista inteira faria o botão
 * prometer 12 com uma busca ativa que devolve 3, e contar depois do status
 * zeraria todos os outros botões (#193/#196).
 */
export function countFinanceEntriesByStatus(
  entries: readonly FinanceEntryRow[],
  today: string = todayIsoDate(),
): Record<FinanceStatusFilter, number> {
  const counts: Record<FinanceStatusFilter, number> = {
    all: entries.length,
    pending: 0,
    paid: 0,
    overdue: 0,
    canceled: 0,
  };
  for (const entry of entries) {
    counts[effectiveFinanceStatus(entry, today)] += 1;
  }
  return counts;
}

// ─── Período e faixa de valor (o modal) ─────────────────────────────────────

/**
 * Presets PRÓPRIOS, e não os de `reportPeriod` — ver o cabeçalho: aqui a coluna
 * é `due_date` e a pergunta olha para a frente.
 *
 * "Este mês" e "Próximos 30 dias" são os dois que o seletor antigo desta tela
 * oferecia; os outros três existem porque a faixa deixou de ser um `Select` de
 * três opções e passou a caber num modal. "Vencidos", que era a quarta opção
 * daquele seletor, NÃO virou período: ele é STATUS, e agora tem botão próprio
 * na faixa — misturar os dois eixos num controle só era o que impedia
 * perguntar "o que venceu ESTE MÊS".
 */
export type FinancePeriodPreset = 'thisMonth' | 'next7' | 'next30' | 'last30' | 'thisYear';

export const FINANCE_PERIOD_PRESETS: readonly FinancePeriodPreset[] = [
  'thisMonth',
  'next7',
  'next30',
  'last30',
  'thisYear',
];

export const FINANCE_PERIOD_LABELS: Record<FinancePeriodPreset, string> = {
  thisMonth: 'Este mês',
  next7: 'Próximos 7 dias',
  next30: 'Próximos 30 dias',
  last30: 'Últimos 30 dias',
  thisYear: 'Este ano',
};

export interface FinanceIsoRange {
  /** 'YYYY-MM-DD' inclusivo. */
  from: string;
  /** 'YYYY-MM-DD' inclusivo. */
  to: string;
}

/**
 * A faixa de datas de um preset, em 'YYYY-MM-DD' pelo calendário LOCAL.
 *
 * `now` é parâmetro para o teste poder congelar o relógio: um fixture que fala
 * em "este mês" sem relógio fixo é sorte, não fixture (#154).
 */
export function resolveFinancePeriod(preset: FinancePeriodPreset, now: Date = new Date()): FinanceIsoRange {
  switch (preset) {
    case 'next7':
      return { from: todayIsoDate(now), to: todayIsoDate(addDays(now, 7)) };
    case 'next30':
      return { from: todayIsoDate(now), to: todayIsoDate(addDays(now, 30)) };
    case 'last30':
      return { from: todayIsoDate(subDays(now, 30)), to: todayIsoDate(now) };
    case 'thisYear':
      return { from: todayIsoDate(startOfYear(now)), to: todayIsoDate(endOfYear(now)) };
    case 'thisMonth':
    default:
      return { from: todayIsoDate(startOfMonth(now)), to: todayIsoDate(endOfMonth(now)) };
  }
}

/**
 * `range` nulo = sem filtro de período. Lançamento SEM data NÃO é recortado:
 * sumir com ele por causa de um carimbo ausente é pior do que mostrá-lo fora da
 * faixa pedida — o usuário não teria como saber que ele existe (#193-d).
 */
export function matchesFinancePeriod(entry: FinanceEntryRow, range: FinanceIsoRange | null): boolean {
  if (!range) return true;
  const due = String(entry?.due_date ?? '');
  if (!due) return true;
  return due >= range.from && due <= range.to;
}

/** `null` em `min`/`max` é SEM LIMITE, nunca zero (ver `listFilters`). */
export function matchesFinanceValue(entry: FinanceEntryRow, range: ListValueRange): boolean {
  return matchesListRange(entry?.amount, range);
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * O STATUS fica de fora: ele tem os botões visíveis logo acima, então já se
 * anuncia sozinho. Contá-lo faria o selo dizer "1" com o modal em branco.
 */
export function countActiveFinanceFilters(
  period: FinancePeriodPreset | null,
  range: ListValueRange,
): number {
  let active = 0;
  if (period) active += 1;
  if (range.min !== null || range.max !== null) active += 1;
  return active;
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

/** O status que NÃO representa dinheiro. */
export const DEAD_FINANCE_STATUSES: ReadonlySet<string> = new Set<FinanceStatus>(['canceled']);

/** `true` quando o recorte É exatamente o status morto (ver `includeDead`). */
export function isDeadFinanceStatusFilter(filter: FinanceStatusFilter): boolean {
  return DEAD_FINANCE_STATUSES.has(filter);
}

export interface FinanceListSummary {
  /** Lançamentos no recorte — todos, inclusive os cancelados. */
  count: number;
  /** Soma dos que ainda representam dinheiro (fora os cancelados). */
  total: number;
  /** Quantos ficaram FORA da soma. Nunca cortado em silêncio (#61). */
  excludedCount: number;
}

export interface SummarizeFinanceListOptions {
  /**
   * Soma TAMBÉM os cancelados.
   *
   * Existe por causa do filtro explícito por "Cancelados": excluir todo mundo
   * ali faria o hero anunciar R$ 0,00 com a lista cheia embaixo — o número mais
   * visível da tela contradizendo o que ela mostra. A exclusão serve para a
   * faixa "Todos" não INFLAR o que há a receber (ou a pagar); quando o usuário
   * pede justamente os cancelados, a pergunta mudou.
   */
  includeDead?: boolean;
  today?: string;
}

/** Resumo do recorte JÁ FILTRADO (busca, status, período e valor aplicados). */
export function summarizeFinanceList(
  entries: readonly FinanceEntryRow[],
  options: SummarizeFinanceListOptions = {},
): FinanceListSummary {
  const today = options.today ?? todayIsoDate();
  let total = 0;
  let excludedCount = 0;

  for (const entry of entries) {
    if (!options.includeDead && DEAD_FINANCE_STATUSES.has(effectiveFinanceStatus(entry, today))) {
      excludedCount += 1;
      continue;
    }
    total += quoteTotalNumber(entry?.amount);
  }

  return { count: entries.length, total, excludedCount };
}

/**
 * O RÓTULO do valor do hero — é ele que torna o número honesto.
 *
 * A soma sai do recorte já filtrado (busca + status + período + valor), então
 * ela muda enquanto o usuário digita. Sem dizer de qual recorte ela saiu,
 * "R$ 12.400,00" sob o título "A receber" lê como "é isso que eu tenho a
 * receber" quando pode ser o recorte de já recebidos com uma busca ativa
 * (#182/#188).
 */
export function financeListTotalLabel(kind: FinanceKind, filter: FinanceStatusFilter): string {
  const income = kind === 'income';
  switch (filter) {
    case 'pending':
      return 'Total pendente';
    case 'paid':
      return income ? 'Total recebido' : 'Total pago';
    case 'overdue':
      return 'Total vencido';
    // O status morto ganha rótulo PRÓPRIO porque, sob o filtro dele, a soma
    // passa a incluí-lo (`includeDead`): dizer "Total a receber" ali faria o
    // número parecer dinheiro que vem quando ele é justamente o oposto.
    case 'canceled':
      return 'Total cancelado';
    default:
      return income ? 'Total a receber' : 'Total a pagar';
  }
}
